import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MailCheck, MailX, TrendingUp, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * R123 — Email Delivery Insights tile.
 *
 * Compact deliverability card for the admin dashboard. Shows last-24h
 * delivered / failed / bounced counts pulled from db.email_events (populated
 * by services_email and the /webhooks/resend receiver).
 */
export default function DeliveryInsightsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-insights");
      setData(data);
    } catch (e) { if (typeof console !== 'undefined') console.debug('components/DeliveryInsightsCard.jsx: swallowed', e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const last = data?.last_24h;
  const rate = last?.success_rate_pct ?? null;
  const rateBadgeTone = rate === null
    ? "border-white/10 text-zinc-400"
    : rate >= 95
      ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/10"
      : rate >= 80
        ? "border-amber-500/30 text-amber-300 bg-amber-500/10"
        : "border-rose-500/30 text-rose-300 bg-rose-500/10";

  return (
    <Card className="p-5 bg-card border-white/10" data-testid="delivery-insights-card">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">Email Delivery · 24h</div>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                 onClick={load} data-testid="delivery-insights-refresh">
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      {!data ? (
        <div className="text-xs text-zinc-500 py-6 text-center">
          {loading ? "Loading…" : "No data yet"}
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-2 mb-3">
            <div className="text-3xl font-semibold font-display tabular-nums"
                 data-testid="delivery-rate">
              {rate !== null ? `${rate}%` : "—"}
            </div>
            <Badge variant="outline" className={`text-[10px] ${rateBadgeTone}`}>
              success rate
            </Badge>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <MiniStat label="Delivered" value={last?.delivered ?? 0}
                       icon={MailCheck} tone="text-emerald-300"
                       testid="delivery-delivered" />
            <MiniStat label="Failed" value={last?.failed ?? 0}
                       icon={MailX} tone="text-rose-300"
                       testid="delivery-failed" />
            <MiniStat label="Bounced" value={last?.bounced ?? 0}
                       icon={TrendingUp} tone="text-amber-300"
                       testid="delivery-bounced" />
          </div>
          <div className="mt-3 text-[10px] text-zinc-500 flex flex-wrap gap-x-3">
            {Object.entries(last?.per_provider || {}).map(([prov, row]) => (
              <span key={prov} data-testid={`delivery-provider-${prov}`}>
                <span className="text-zinc-400">{prov}</span>:{" "}
                {row.delivered}✓ {row.failed > 0 && <span className="text-rose-300">{row.failed}✗</span>}
              </span>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

function MiniStat({ label, value, icon: Icon, tone, testid }) {
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.02] p-2.5">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500">
        <Icon className={`h-3 w-3 ${tone}`} />{label}
      </div>
      <div className={`text-xl font-semibold tabular-nums mt-0.5 ${tone}`} data-testid={testid}>
        {value}
      </div>
    </div>
  );
}
